import {
  COURSES as UEC_COURSES,
  // SCHEDULE as UEC_SCHEDULE,
  SUBJECT_ICONS as UEC_SUBJECT_ICONS,
  TEACHERS as UEC_TEACHERS,
} from "../../LandingPage/UEC2026Data";
import { TEACHERS as SPM_TEACHERS } from "../../LandingPage/SPM2026Data";
import { SPM_NOTES, SPM_SCHEDULE } from "./SPMResources";
import { UEC_NOTES, UEC_SCHEDULE } from "./UECResources";
import type {
  ExamHubData,
  ExamHubResource,
  ExamHubSession,
  ExamHubSubject,
  ExamHubTeacher,
  ExamType,
} from "../../../types/examHub";

const toSubjectId = (subject: string) =>
  subject
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-|-$/g, "");

const toResources = (resources: typeof UEC_NOTES | typeof SPM_NOTES) =>
  resources.map(
    (resource): ExamHubResource => ({
      ...resource,
      subjectId:
        resource.type === "schedule"
          ? "general"
          : toSubjectId(resource.subject),
      type: resource.type as ExamHubResource["type"],
    }),
  );

const toSubjects = (
  sessions: ExamHubSession[],
  resources: ExamHubResource[],
): ExamHubSubject[] => {
  const subjects = new Map<string, ExamHubSubject>();

  [...sessions, ...resources]
    .filter((item) => item.subjectId !== "general")
    .forEach((item) => {
      if (!subjects.has(item.subjectId)) {
        subjects.set(item.subjectId, {
          id: item.subjectId,
          name: item.subject,
          color: item.subjectColor || "#64748B",
        });
      }
    });

  return [...subjects.values()];
};

const uecTeachers: ExamHubTeacher[] = UEC_TEACHERS.map((teacher, index) => ({
  id: `uec_teacher_${index + 1}`,
  name: teacher.name,
  subjectIds: [toSubjectId(teacher.subject)],
  // qualification: teacher.credentials[0] || "UEC revision class teacher",
  experience: teacher.credentials[teacher.credentials.length - 1] || "",
  bio: `• ${teacher.credentials.join("\n• ")}`,
  avatar: teacher.avatar,
  avatarColor: teacher.color,
}));

const uecSessions: ExamHubSession[] = UEC_SCHEDULE.map((session, index) => ({
  id: `uec_session_${index + 1}`,
  subjectId: toSubjectId(session.subject),
  subject: session.subject,
  topic: `${session.subject} UEC Ultimate Revision Class`,
  teacherId:
    uecTeachers.find((teacher) =>
      teacher.subjectIds.includes(toSubjectId(session.subject)),
    )?.id || "",
  date: session.date,
  day: session.day,
  time: session.time,
  duration: session.duration,
  zoomLink: session.zoomLink,
  status: session.status,
  subjectColor: session.subjectColor,
  subjectEmoji: UEC_SUBJECT_ICONS[session.subject] ?? "",
}));

const spmTeachers: ExamHubTeacher[] = SPM_TEACHERS.map((teacher) => {
  const subjectIds =
    teacher.id === "spm_teacher_math"
      ? [toSubjectId("Mathematics"), toSubjectId("Additional Mathematics")]
      : [toSubjectId(teacher.subject)];

  return {
    id: teacher.id,
    name: teacher.name,
    subjectIds,
    // qualification: teacher.credentials[0] || "SPM revision class teacher",
    experience: teacher.credentials[1] || "",
    bio: `• ${teacher.credentials.join("\n• ")}`,
    avatar: teacher.avatar,
    avatarColor: teacher.color,
  };
});

const spmSessions: ExamHubSession[] = SPM_SCHEDULE.map((session) => ({
  ...session,
  subjectId: toSubjectId(session.subject),
  status: session.status as ExamHubSession["status"],
}));

const uecResources = toResources(UEC_NOTES);
const spmResources = toResources(SPM_NOTES);

const UEC_EXAM_HUB_DATA: ExamHubData = {
  id: "UEC",
  label: "UEC",
  subjects: toSubjects(uecSessions, uecResources),
  teachers: uecTeachers,
  sessions: uecSessions,
  resources: uecResources,
};

const SPM_EXAM_HUB_DATA: ExamHubData = {
  id: "SPM",
  label: "SPM",
  subjects: toSubjects(spmSessions, spmResources),
  teachers: spmTeachers,
  sessions: spmSessions,
  resources: spmResources,
};

export const EXAM_HUB_DATA: Record<ExamType, ExamHubData> = {
  UEC: UEC_EXAM_HUB_DATA,
  SPM: SPM_EXAM_HUB_DATA,
};

const uecCourseValueByLabel = new Map(
  UEC_COURSES.map((course) => [course.label, course.value]),
);

const uecCourseValues = new Set(UEC_COURSES.map((course) => course.value));

export const getUecSubjectValue = (subject: unknown) => {
  const normalizedSubject = String(subject || "").trim();

  return uecCourseValues.has(normalizedSubject)
    ? normalizedSubject
    : uecCourseValueByLabel.get(normalizedSubject) || normalizedSubject;
};

export const getRegisteredSubjectValues = (subjects: unknown) => {
  if (!Array.isArray(subjects)) return [];

  return subjects
    .map(getUecSubjectValue)
    .filter((subject): subject is string => Boolean(subject));
};
